/**
 * Credential mention — trigger `@` urmat de query. Suggestion peste
 * credentialele tenantului, cu scorare `startsWith` > `includes`. La
 * select inserează CredentialMentionNode cu attrs {id, title}.
 *
 * Motiv: la zeci de credentiale, lista inline în /slash devine ilizibilă.
 * Cu trigger separat, slash rămâne curat (formatare/blocuri), iar @ e
 * dedicat pentru mențiuni de credențiale.
 */
import { Extension, type Editor } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';
import { useTranslation } from 'react-i18next';

const CRED_MENU_KEY = new PluginKey('cerbes-cred-menu');

import { makeSuggestionRenderer } from '@/features/notes/suggestionRenderer';
import { credentialsApi, type CredentialSummary } from '@/api/credentials';
import { KeyIcon } from '@/features/notes/icons';

export function createCredentialMenu(tenantId: string): Extension {
  let cache: CredentialSummary[] | null = null;
  let cachedAt = 0;

  const getCreds = async (): Promise<CredentialSummary[]> => {
    const now = Date.now();
    if (cache && now - cachedAt < 30_000) return cache;
    if (!tenantId) return [];
    try {
      const list = await credentialsApi.list(tenantId);
      cache = list;
      cachedAt = now;
      return list;
    } catch {
      return [];
    }
  };

  return Extension.create({
    name: 'credentialMenu',
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          pluginKey: CRED_MENU_KEY,
          char: '@',
          startOfLine: false,
          allowSpaces: false,
          items: async ({ query }: { query: string }) => {
            const all = await getCreds();
            const q = query.trim().toLowerCase();
            if (!q) return all.slice(0, 10);
            // Scoring: startsWith > includes pe title, apoi type.
            const scored = all
              .map((c) => {
                const t = c.title.toLowerCase();
                const y = c.type.toLowerCase();
                let s = -1;
                if (t.startsWith(q)) s = 3;
                else if (t.includes(q)) s = 2;
                else if (y.includes(q)) s = 1;
                return { c, s };
              })
              .filter((x) => x.s >= 0)
              .sort((a, b) => b.s - a.s);
            return scored.slice(0, 10).map((x) => x.c);
          },
          command: ({
            editor,
            range,
            props,
          }: {
            editor: Editor;
            range: { from: number; to: number };
            props: CredentialSummary;
          }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent([
                {
                  type: 'credentialMention',
                  attrs: { id: props.id, title: props.title },
                },
                { type: 'text', text: ' ' },
              ])
              .run();
          },
          render: makeSuggestionRenderer(CredList),
        } as SuggestionOptions),
      ];
    },
  });
}

function CredList(props: {
  items: CredentialSummary[];
  command: (item: CredentialSummary) => void;
  selectedIndex: number;
}) {
  const { items, command, selectedIndex } = props;
  const { t } = useTranslation();
  if (items.length === 0) {
    return (
      <div className="bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-md shadow-lg px-3 py-2 text-xs text-[var(--text-muted)]">
        {t('notes_blocks.cred_menu.empty')}
      </div>
    );
  }
  return (
    <div className="bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-md shadow-lg py-1 max-h-64 overflow-auto min-w-[280px]">
      {items.map((item, idx) => {
        const active = idx === selectedIndex;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => command(item)}
            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
              active
                ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                : 'text-[var(--text)] hover:bg-[var(--bg-hover)]'
            }`}
          >
            <span
              className={`flex items-center justify-center w-6 h-6 rounded flex-shrink-0 ${
                active
                  ? 'text-brand-700 dark:text-brand-300'
                  : 'text-[var(--text-muted)]'
              }`}
            >
              <KeyIcon />
            </span>
            <span className="flex-1 min-w-0">
              <div className="font-medium truncate">{item.title}</div>
              <div className="text-[10px] text-[var(--text-muted)] truncate">
                {item.type}
              </div>
            </span>
          </button>
        );
      })}
    </div>
  );
}
